import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { DeviceType } from '~/types/device-type'

import Button from '~/components/Buttons/Button'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import { FORM_ERRORS } from '~/const'
import { iapKeys, useCreateFeatureMutation } from '~/queries/iap'
import { displayToast } from '~/utils/toast'

interface FormValues {
  featureId: string
}

const defaultValues: FormValues = {
  featureId: '',
}

const validationSchema = Yup.object().shape({
  featureId: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

interface Props {
  appKey?: string
  deviceType?: DeviceType
  isOpen: boolean
  onClose: VoidFunction
}

const CreateFeatureModal = ({ appKey, deviceType, isOpen, onClose }: Props): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const {
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    register,
    reset,
  } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  /** Queries & Mutations */

  const { mutateAsync: createFeature } = useCreateFeatureMutation()

  /** Handlers */

  const handleFormSubmit = async ({ featureId }: FormValues) => {
    if (appKey && deviceType) {
      try {
        await createFeature({
          appKey,
          deviceType,
          featureId,
        })
        await queryClient.invalidateQueries({ queryKey: iapKeys.featuresList(appKey, deviceType) })
        handleModalClose()
        displayToast('Feature successfully created', 'success')
      } catch (e) {
        captureException(e)
        displayToast('Feature could not be created', 'error')
      }
    }
  }

  const handleModalClose = () => {
    reset()
    onClose()
  }

  /** Render */

  return (
    <Modal isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Create Feature</ModalHeader>
      <ModalBody>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <TextInput
            label="Feature key"
            {...register('featureId')}
            error={errors.featureId?.message}
            placeholder="Enter a feature key"
            required
          />
        </form>
      </ModalBody>
      <ModalFooter>
        <Button ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!isDirty || isSubmitting} onClick={handleSubmit(handleFormSubmit)} type="submit">
          Create
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default CreateFeatureModal
